import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { StaffFormModal } from './StaffFormModal'
import { useStaff, type StaffDto } from '../../../viewmodels/useStaff'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /iam/staff — list operators + Add/Edit modal.
 *
 * Permissions checked server-side (Layer 2c via iam.staff
 * screen_actions). The page surfaces 403s as an inline error.
 *
 * Delete is a SOFT delete (status → deleted). The server blocks
 * deleting your own account and the last admin.
 */
export function StaffPage(): JSX.Element {
  const {
    items, total, page, pageSize, isLoading, error,
    setPage, reload,
    userTypeOptions, create, update, remove
  } = useStaff()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<StaffDto | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: StaffDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: StaffDto): Promise<void> => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Delete staff "${row.email}"? This cannot be undone.`)
    ) {
      return
    }
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Staff</h1>
        {can(SCREENCODES.IAMSTAFF, 'add') ? <Button onClick={openCreate}>Add staff</Button> : null}
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}

      {isLoading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState onAdd={can(SCREENCODES.IAMSTAFF, 'add') ? openCreate : null} />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <StaffFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        userTypeOptions={userTypeOptions}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

function EmptyState({ onAdd }: { readonly onAdd: (() => void) | null }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No staff yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Invite your first operator — they&rsquo;ll get a welcome email with a one-time
        link to set their password.
      </p>
      {onAdd !== null ? (
        <div className="mt-4">
          <Button onClick={onAdd}>Add staff</Button>
        </div>
      ) : null}
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: StaffDto[]
  readonly onEdit: (row: StaffDto) => void
  readonly onDelete: (row: StaffDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.IAMSTAFF, 'edit')
  const canDelete = can(SCREENCODES.IAMSTAFF, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>User type</Th>
            <Th>Status</Th>
            <Th>Last login</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <Td>
                <span className="text-charcoal">
                  {[row.first_name, row.last_name].filter((x) => x !== null && x.length > 0).join(' ') || '—'}
                </span>
              </Td>
              <Td><span className="font-mono text-text-secondary text-body-xs">{row.email}</span></Td>
              <Td>
                <span className="font-mono text-body-xs">{row.user_type_code ?? '—'}</span>
              </Td>
              <Td>
                <StatusBadge status={row.status} isTemp={row.is_temp_password} />
              </Td>
              <Td>
                <span className="font-body text-body-xs text-text-secondary">
                  {row.last_login_at !== null ? new Date(row.last_login_at).toLocaleString() : '—'}
                </span>
              </Td>
              <Td className="text-right whitespace-nowrap space-x-2">
                {canEdit ? (
                  <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                    Edit
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(row)}
                    disabled={deletingId === row.id}
                  >
                    {deletingId === row.id ? 'Deleting…' : 'Delete'}
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

function StatusBadge({
  status,
  isTemp
}: {
  readonly status: StaffDto['status']
  readonly isTemp: boolean
}): JSX.Element {
  if (status === 'invited') return <Badge tone="warning">Invited</Badge>
  if (status === 'suspended') return <Badge tone="error">Suspended</Badge>
  if (status === 'deleted') return <Badge tone="neutral">Deleted</Badge>
  // active
  return isTemp ? <Badge tone="warning">Active (temp password)</Badge> : <Badge tone="success">Active</Badge>
}
